import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LayoutDashboard, PlusCircle, Receipt, BarChart3, LogOut } from 'lucide-react';

export default function Layout({ children }) {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/consumption', icon: BarChart3, label: 'Monthly Consumption' },
    { to: '/invoices', icon: Receipt, label: 'Invoices' },
    { to: '/estimate/new', icon: PlusCircle, label: 'New Estimate' },
  ];

  const handleSignOut = () => {
    instance.logoutRedirect();
  };

  return (
    <div className="min-h-screen bg-black" data-testid="app-layout">
      <header className="border-b border-zinc-900 bg-black/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2 group" data-testid="app-logo">
            <img src="/aays-logo.png" alt="Aays" className="h-8 w-auto invert" />
            <span className="text-zinc-600 font-light text-lg select-none">|</span>
            <span className="font-manrope font-bold text-base tracking-tight text-white group-hover:text-brand-400 transition-colors">
              Revenue Management
            </span>
          </Link>

          <nav className="flex items-center gap-1" data-testid="main-nav">
            {navItems.map(({ to, icon: Icon, label }) => {
              const isActive = location.pathname === to || 
                (to === '/estimate/new' && location.pathname.startsWith('/estimate')) ||
                (to === '/invoices' && location.pathname === '/invoices') ||
                (to === '/consumption' && location.pathname === '/consumption');
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-brand-400 bg-brand-500/10'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </Link>
              );
            })}

            {account && (
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-zinc-800">
                <span className="text-zinc-500 text-xs hidden md:inline truncate max-w-[150px]" data-testid="user-display-name">
                  {account.name || account.username}
                </span>
                <button
                  onClick={handleSignOut}
                  data-testid="sign-out-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-zinc-400 hover:text-red-400 hover:bg-zinc-900 transition-all duration-200"
                  title="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </div>
            )}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {children}
      </main>
    </div>
  );
}
